import { DevLinkProvider } from '@/components/devlink';
import { AppLink } from '@/components/links/AppLink/AppLink';
import { DeviceProvider } from '@/contexts/DeviceContext';
import { ManifestProvider } from '@/contexts/ManifestContext';
import { NavigationProvider } from '@/contexts/NavigationContext';
import AppShell from '@/layout';
import MissingManifest from '@/layout/MissingManifest';
import ToastNotification from '@/layout/ToastNotification';
import { getRouteConfig } from '@/routes';
import { Manifest } from '@/types';
import i18n from 'i18next';
import { lazy, Suspense, useMemo } from 'react';
import { I18nextProvider } from 'react-i18next';
import { Route, Switch } from 'wouter-preact';

interface AppProps {
  manifest: Manifest | undefined;
}

const App = ({ manifest }: AppProps) => {
  // `lazy` once per locale set, not once per render. It returns a fresh
  // component type every call, and a new type at the same position is not an
  // update but a remount: the page loses its state and rebuilds itself,
  // which on the supporter map means a new Mapbox instance at world view in
  // the middle of whatever flight was running. Above the guard below so the
  // hook order does not depend on the manifest having arrived.
  const routes = useMemo(
    () =>
      getRouteConfig(manifest?.locale.supported ?? []).map(
        ({ key, path, component }) => ({
          key,
          path,
          Component: lazy(() => component),
        }),
      ),
    [manifest?.locale.supported],
  );

  if (!manifest) {
    return <MissingManifest />;
  }

  return (
    <ManifestProvider manifest={manifest}>
      <DeviceProvider>
        <I18nextProvider i18n={i18n} defaultNS={manifest?.locale.slug}>
          <NavigationProvider>
            <AppShell>
              <ToastNotification />
              <Suspense fallback={undefined}>
                <DevLinkProvider
                  renderLink={({ href, target, className, children }) => (
                    <AppLink href={href} target={target} className={className}>
                      {children}
                    </AppLink>
                  )}
                >
                  <Switch>
                    {routes.map(({ key, path, Component }) => (
                      <Route key={key} path={path} component={Component} />
                    ))}
                  </Switch>
                </DevLinkProvider>
              </Suspense>
            </AppShell>
          </NavigationProvider>
        </I18nextProvider>
      </DeviceProvider>
    </ManifestProvider>
  );
};

export default App;
